// src/utils/leoYear.js
// Leo/Lions council-year helpers (the club's program year runs July 1 -
// June 30, not the calendar year) plus a SLIIT-ID-based graduation
// estimate. Used by ClubStatistics.jsx ("Club Dashboard") so the
// membership-by-year breakdown never goes stale (it used to be hardcoded
// date strings that silently became wrong once "the current year" changed)
// and so "graduated" reflects actual academic standing instead of how long
// ago someone joined the Leo club, which can be very different things.

// The earliest council year the dashboard tracks. Chosen to match the
// portal's own roster history, not derived from data, so one bad/garbled
// RegDate can't stretch the table with a bogus extra row.
export const EARLIEST_COUNCIL_YEAR = 2020;

// SLIIT undergraduate programs are 4 years; this is the one duration the
// club actually asked for. There's no data source mapping a SLIIT ID
// prefix to its specific program length, so this is an estimate applied
// uniformly -- treat "graduated" as "likely graduated," not a confirmed fact.
export const PROGRAM_LENGTH_YEARS = 4;

// A council year "2025-26" starts 2025-07-01 00:00:00 and runs up to (but
// not including) 2026-07-01 00:00:00 -- i.e. it flips at midnight on July 1,
// matching how the club's own program year is defined.
export function getCouncilYearStart(date) {
  const d = date instanceof Date ? date : new Date(date);
  const month = d.getMonth(); // 0 = Jan ... 5 = Jun, 6 = Jul ... 11 = Dec
  return month >= 6 ? d.getFullYear() : d.getFullYear() - 1;
}

export function getCurrentCouncilYearStart() {
  return getCouncilYearStart(new Date());
}

export function formatCouncilYearLabel(startYear) {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

// [start, end) as real Date objects -- end is exclusive (the next year's
// July 1st) so a registration timestamped exactly at a year boundary is
// never double-counted or dropped.
export function getCouncilYearRange(startYear) {
  return {
    start: new Date(startYear, 6, 1, 0, 0, 0, 0),
    end: new Date(startYear + 1, 6, 1, 0, 0, 0, 0),
  };
}

export function isDateInCouncilYear(date, startYear) {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return false;
  const { start, end } = getCouncilYearRange(startYear);
  return d >= start && d < end;
}

// Every council year from EARLIEST_COUNCIL_YEAR through the current one,
// oldest first. Grows by one entry every July -- nothing to update by hand.
export function getCouncilYearOptions() {
  const current = getCurrentCouncilYearStart();
  const years = [];
  for (let y = EARLIEST_COUNCIL_YEAR; y <= current; y++) {
    years.push({ startYear: y, label: formatCouncilYearLabel(y), ...getCouncilYearRange(y) });
  }
  return years;
}

// "IT21808784" -> 21 -> 2021. Returns null for anything that doesn't start
// with 2 letters + 2 digits (staff records, malformed IDs, non-student
// members) so callers can treat "unknown" distinctly from "graduated".
export function parseSliitEnrollmentYear(sliitId) {
  if (!sliitId) return null;
  const match = String(sliitId).trim().match(/^[A-Za-z]{2}(\d{2})\d*$/);
  if (!match) return null;
  return 2000 + parseInt(match[1], 10);
}

export function getExpectedGraduationYear(sliitId) {
  const enrollmentYear = parseSliitEnrollmentYear(sliitId);
  return enrollmentYear === null ? null : enrollmentYear + PROGRAM_LENGTH_YEARS;
}

// A member is "likely graduated" once the current council year has reached
// their expected graduation year (IT21 -> expects to graduate in 2025, so
// by the 2025-26 council year or later they should have left SLIIT).
export function isLikelyGraduatedBySliitId(sliitId, asOfCouncilYearStart = getCurrentCouncilYearStart()) {
  const gradYear = getExpectedGraduationYear(sliitId);
  return gradYear !== null && gradYear <= asOfCouncilYearStart;
}
